// =========================================================================
// 1D 円環レイアウト
// =========================================================================
// spec/closing_river_web_mvp_v2.md 「レイアウト (1D 円環)」 のコード例を
// そのまま実装。12 時方向開始、半径 = min(w, h) * 0.36 (余白配慮)。
//
// 担当: W3 (pure renderer)
// 利用側: canvas.ts (BoardViewModel.nodes に position が無いときの fallback)
// =========================================================================

/**
 * 与えられた node id 列に対して、1D 円環上の (x, y) 座標を返す。
 *
 * - 12 時方向 (theta = -pi/2) を 0 番目の開始角度とする
 * - 円周上に nodeIds.length 個を等間隔配置
 * - 半径は min(canvasWidth, canvasHeight) * 0.36 (ノード半径 28px + 余白を考慮)
 *
 * @param nodeIds  配置対象のノード id 列 (順序が円環の位相を決める)
 * @param canvasWidth  描画領域の幅 (CSS px)
 * @param canvasHeight 描画領域の高さ (CSS px)
 * @returns id をキー、`{x, y}` を値とするオブジェクト
 */
export function circleLayout(
  nodeIds: readonly number[],
  canvasWidth: number,
  canvasHeight: number,
): Record<number, { x: number; y: number }> {
  const cx = canvasWidth / 2;
  const cy = canvasHeight / 2;
  const radius = Math.min(canvasWidth, canvasHeight) * 0.36;
  const n = nodeIds.length;
  const out: Record<number, { x: number; y: number }> = {};

  if (n === 0) {
    return out;
  }

  // n === 1 のとき、勝利終端 (中央 1 ノード) を表現する想定。
  // 円環として展開しても 12 時方向 1 点になるが、終端は中央に置きたいので
  // 中心座標を返す (renderer 側で半径 56px の terminal node を中央配置できる)。
  if (n === 1) {
    out[nodeIds[0]!] = { x: cx, y: cy };
    return out;
  }

  for (let i = 0; i < n; i++) {
    const theta = (i / n) * 2 * Math.PI - Math.PI / 2; // 12 時方向開始
    out[nodeIds[i]!] = {
      x: cx + radius * Math.cos(theta),
      y: cy + radius * Math.sin(theta),
    };
  }
  return out;
}
